import { normalizeDomain } from '../../utils/domain/domain';
import type { Domain } from './types';

export type DomainMatchStrategy = 'exact' | 'normalized';

function normalizeInputDomain(domain: string): string {
    return domain.trim().toLowerCase();
}

/**
 * Build lookup candidates for domains table resolution.
 * Order matters: exact host first, normalized root fallback second.
 */
export function getDomainCandidates(rawDomain: string): string[] {
    const requested = normalizeInputDomain(rawDomain);
    if (!requested) return [];

    const normalized = normalizeDomain(requested);
    if (requested === normalized) {
        return [requested];
    }
    return [requested, normalized];
}

/**
 * Pick the best matching domain row for a hostname.
 * Exact host match has priority over normalized root fallback.
 */
export function selectDomainMatch(
    rawDomain: string,
    rows: Domain[]
): { domain: Domain; matchStrategy: DomainMatchStrategy } | null {
    const requested = normalizeInputDomain(rawDomain);
    if (!requested || rows.length === 0) return null;

    const normalized = normalizeDomain(requested);
    const byValue = new Map(rows.map((row) => [normalizeInputDomain(row.domain), row]));

    const exactMatch = byValue.get(requested);
    if (exactMatch) {
        return { domain: exactMatch, matchStrategy: 'exact' };
    }

    const normalizedMatch = byValue.get(normalized);
    if (normalizedMatch) {
        return { domain: normalizedMatch, matchStrategy: 'normalized' };
    }

    return null;
}

/**
 * Check whether a requested host should use the resolved config domain.
 */
export function doesDomainMatchResolver(
    requestedDomain: string,
    resolverDomain: string,
    matchStrategy: DomainMatchStrategy
): boolean {
    const requested = normalizeInputDomain(requestedDomain);
    const resolved = normalizeInputDomain(resolverDomain);
    if (!requested || !resolved) return false;

    if (matchStrategy === 'normalized') {
        return normalizeDomain(requested) === normalizeDomain(resolved);
    }

    return requested === resolved;
}
